import type { AppBundle } from '../../utils/app-bundles';
import type { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import type { AccountPermission } from '@customerio/hydra-permissions';
import type { UserRole } from '@/features/roles';
import type { UsersSideNavPreferences } from '@customerio/hydra-ui-api';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';

export const VerificationState = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Unsubmitted: 'unsubmitted',
} as const;
export type VerificationState = (typeof VerificationState)[keyof typeof VerificationState];

export const VerificationReviewState = {
  Pending: 'pending',
  Enriching: 'enriching',
  Deferred: 'deferred',
  Review: 'review',
  Approved: 'approved',
  Rejected: 'rejected',
  NeedsMoreInfo: 'needs_more_info',
} as const;
export type VerificationReviewState = (typeof VerificationReviewState)[keyof typeof VerificationReviewState];

export interface Account {
  id: number;
  created_at: number;
  name: string;
  /** The account owner's email — the default test-delivery address. */
  email: string;
  is_sample_account_access: boolean;
  is_masq: boolean;
  write_access: boolean;
  is_own_account: boolean;
  feature_flags: Record<string, boolean>;
  logo_url: string;
  plan_slug: string;
  premium: boolean;
  premium_trial: boolean;
  /** Account-level partner flag (set in Flightdeck). Gates the Partner section. */
  partner_account: boolean;
  plan_features: PlanFeature[];
  gated_features: GatedFeatures;
  admin: boolean;
  product_activations: AppBundle[];
  region: Region;
  support_tier: SupportTier;
  suspend_trial_at?: number;
  /** Unix timestamp (seconds) when a canceled account is scheduled for deletion. */
  delete_at?: number;
  account_state: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  access_granted_until?: number;
  access_granted_by?: string;
  access_granted_on?: number;
  data_center: 'us' | 'eu';
  enable_troubleshooting_cookies: boolean;
  has_csm: boolean;
  cs_access_granted_until?: number;
  cs_access_granted_by?: string;
  cs_access_granted_on?: number;
  ordway_id?: string;
  billing_vendor?: BillingVendor;
  read_only: boolean;
  verification_state: VerificationState;
  verification_review_state?: VerificationReviewState;
  sandbox: boolean;
  /**
   * Account-level customer opt-in settings. `ai_enabled` gates Customer.io AI
   * (formerly the `ai_enabled` feature flag). Omitted keys are treated as false.
   */
  public_settings?: AccountPublicSettings;
}

/** Account-level customer opt-in settings, stored in `accounts.public_settings`. */
export interface AccountPublicSettings {
  ai_enabled?: boolean;
  use_global_ai_endpoint?: boolean;
}

export interface RawAccount {
  id: string;
  name: string;
  email: string;
  has_data: boolean;
  plan_price: number;
  plan_period_start: number;
  plan_period_end: number;
  plan_include_churn: boolean;
  next_invoice_date?: number | null;
  setup_data: number;
  setup_email: number;
  system_notification: string;
  account_state: string;
  sso_setting: string;
  confirmed: boolean;
  verification_state: VerificationState;
  verification_review_state?: VerificationReviewState;
  admin: boolean;
  current_user_id: string;
  invoice_email: string;
  legal_email: string;
  technical_email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  logo_url: string;
  premium: boolean;
  premium_trial: boolean;
  partner_account?: boolean;
  has_csm: boolean;
  access_granted_until: number;
  access_granted_by?: string;
  access_granted_on: number;
  cs_access_granted_until: number;
  cs_access_granted_by?: string;
  cs_access_granted_on: number;
  rd_access_granted_by?: string;
  rd_access_granted_on: number;
  support_tier: SupportTier;
  ordway_id?: string;
  billing_vendor?: BillingVendor;
  session_duration: number;
  environment_ids: string[];
  created_at: number;
  feature_flags: Record<string, boolean>;
  plan_slug: string;
  cancel_trial_at?: number;
  suspend_trial_at?: number;
  suspend_incomplete_subscription_at?: number;
  two_factor_authentication_required: boolean;
  delete_at?: number;
  can_reactivate: boolean;
  is_sample_account_access: boolean;
  is_masqueraded: boolean;
  max_session_duration_hours?: number;
  data_center: 'us' | 'eu';
  enable_troubleshooting_cookies: boolean;
  read_only: boolean;
  gated_features: GatedFeatures;
  product_activations?: AppBundle[];
  custom_smtp_locked: boolean;
  is_paused: boolean;
  sandbox?: boolean;
  public_settings?: AccountPublicSettings;
}

interface PlanFeatureInfo {
  expired: boolean;
  enabled: boolean;
  showPremiumLabel: boolean;
  limit: number | false;
  expiresAt: number | null;
}

export interface AugmentedAccount extends Account {
  // active products as flags for easy access
  activeProducts: Record<AppBundle, boolean>;
  // plan features computed property
  planFeatures: Record<PlanFeature, PlanFeatureInfo>;
  // support team access enabled (computed from access_granted_until)
  support_team_access_enabled: boolean;
  // success team access enabled (computed from cs_access_granted_until)
  success_team_access_enabled: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  account_id: number;
  last_cio_workspace_id: string;
  account_permissions: AccountPermission[];
  roles: UserRole[];
  masq_permissions: UserMasqPermission;
  can_masquerade: boolean;
  can_masquerade_write: boolean;
  user_flags: { [key: string]: boolean };
  settings?: UserSettings;
}

export interface RawUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  accessible_workspace_ids: string[];
  accessible_environment_ids: string[];
  account_permissions: AccountPermission[];
  roles?: UserRole[];
  last_environment_id: string;
  created_at: number;
  last_sign_in_at: number;
  current_sign_in_at: number;
  import_max_size_bytes: number;
  two_factor_authentication_enabled?: boolean;
  two_factor_authentication_email_enabled?: boolean;
  external_identity_provider: string;
  external_identity_email: string;
  user_flags: { [key: string]: boolean };
  google_drive_credential?: { account: string };
  can_masquerade?: boolean;
  can_masquerade_write?: boolean;
  masq_permissions?: UserMasqPermission;
  settings?: UserSettings;
}

// Mirrors services_domains_users_ThemePreferences; can be swapped for the generated
// `UsersThemePreferences` type once the hydra-ui-api client is regenerated post-merge.
export interface UsersThemePreferences {
  color_scheme: ColorSchemePreference;
}

export interface UserSettings {
  side_nav_preferences?: UsersSideNavPreferences;
  theme_preferences?: UsersThemePreferences;
}

export interface AugmentedUser extends User {
  name: string; // computed property for full name
  // masq permissions computed properties
  isSuperUser: boolean;
  canReadOnlyMasq: boolean;
  canReadWriteMasq: boolean;
  noMasqAccess: boolean;
  masqLevelNotSet: boolean;
  hasReadMasqAccess: boolean;
}

export type SupportTier = 'community' | 'email' | 'chat' | '';

/** Billing system backing the account. Mirrors the Journeys UI account model. */
export type BillingVendor = 'legacy_stripe' | 'ordway' | 'metronome';

export const AccountState = {
  Free: 'free',
  Current: 'current',
  Canceled: 'canceled',
  Suspended: 'suspended',
  Overdue: 'overdue',
  PaymentPending: 'payment_pending',
  Deleted: 'deleted',
} as const;

type AccountState = (typeof AccountState)[keyof typeof AccountState];

export interface PlanFeatureLimit {
  limit?: number;
  days?: number;
  start_at?: Date | number;
  expired?: boolean;
  expiresAt?: number;
}

export interface GatedFeatures {
  [key: string]: PlanFeatureLimit | Record<string, never> | undefined;
}

export const Region = {
  US: 0,
  EU: 1,
} as const;

export type Region = (typeof Region)[keyof typeof Region];

export const PlanFeature = {
  Workspaces: 'workspaces',
  WarehouseSync: 'warehouse_sync',
  Collections: 'collections',
  ObjectTypes: 'object_types',
  Messaging: 'messaging',
  MaxObjectJourneys: 'max_object_journeys',
  SupportTierChat: 'support_tier_chat',
  SupportTierCommunity: 'support_tier_community',
  SupportTierEmail: 'support_tier_email',
  PremiumTrial: 'premium_trial',
  CDPPremiumIntegration: 'cdp_premium_integrations',
  AnonymousInApp: 'anonymous_in_app',
  RoutinesActive: 'routines_active',
  CustomSkills: 'custom_skills',
  AuditLogs: 'audit_logs',
  DeliverabilityAnalytics: 'deliverability_analytics',
  CustomRoles: 'custom_roles',
  Geofences: 'geofences',
  // Gate the workspace role redaction options (mirrors the Ember account model's
  // `sensitive_attributes` / `hide_all_attributes` plan features).
  SensitiveAttributes: 'sensitive_attributes',
  HideAllAttributes: 'hide_all_attributes',
} as const;

export type PlanFeature = (typeof PlanFeature)[keyof typeof PlanFeature];

interface IdentifiersConfig {
  cio_id: number;
  id: number;
  email: number;
}

export interface Workspace {
  id: string;
  account_id: string;
  workspace_id: string;
  name: string;
  delivery_settings: string;
  delivery_email: string;
  user_ids: string[];
  hex_code: string;
  created_at: number;
  identifier_type: string;
  identifiers?: IdentifiersConfig;
  type: string;
  stop_processing: boolean;
  email_open_tracking: boolean;
  subscription_center_enabled: boolean;
  email_delivery_disabled: boolean;
}

interface BaseSearchResult {
  doc_id: string;
  kind: string;
  title: string;
  url: string;
  other?: Record<string, any>;
  /** Vertex AI relevance score (0–1, bucketed). Present when the backend sets returnRelevanceScore=true; omitted when Vertex does not return it for a given result. */
  relevance_score?: number;
  /**
   * Matched-text excerpts (with <b> highlight tags) returned by Discovery Engine for
   * content-bearing datastores — templates, snippets, and anonymous in-app. Absent for
   * structured entities and when the match was on a field rather than the body. Mirrors the
   * backend's base SearchResult.Snippets (services/universal_search, json: "snippets,omitempty").
   */
  snippets?: string[];
}

export type CampaignType =
  | 'seg_attr'
  | 'transactional'
  | 'behavioral'
  | 'triggered_broadcast'
  | 'date'
  | 'form'
  | 'webhook'
  | 'object'
  | 'relationship'
  | 'none';

interface CampaignResult extends BaseSearchResult {
  kind: 'campaign';
  other: {
    campaign_type: CampaignType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type ActionType =
  | 'email_action'
  | 'delay_seconds_action'
  | 'delay_time_window_action'
  | 'split_randomized_action'
  | 'webhook_action'
  | 'twilio_action'
  | 'urban_airship_action'
  | 'slack_action'
  | 'attribute_update_action'
  | 'filter_match_delay_action'
  | 'grace_period_action'
  | 'push_action'
  | 'conditional_wait_action'
  | 'randomized_delay_action'
  | 'conditional_branch_action'
  | 'multi_split_branch_action'
  | 'random_cohort_branch_action'
  | 'exit_action'
  | 'static_seg_update_action'
  | 'collection_query_action'
  | 'create_event_action'
  | 'multi_lang_branch_action'
  | 'batch_update_action'
  | 'in_app_action'
  | 'trigger'
  | 'llm_action'
  | 'line_action'
  | 'inbox_action'
  | 'whatsapp_action';

interface ActionResult extends BaseSearchResult {
  kind: 'action';
  other: {
    action_type: ActionType;
    campaign_id: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    campaign_name?: string;
    campaign_url?: string;
  };
}

type SegmentType = 'static' | 'dynamic' | 'temporary' | 'hidden';

interface SegmentResult extends BaseSearchResult {
  kind: 'segment';
  other: {
    segment_type: SegmentType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type TemplateType =
  | 'email'
  | 'webhook'
  | 'twilio'
  | 'urban_airship'
  | 'slack'
  | 'push'
  | 'create_event'
  | 'attribute_update'
  | 'batch_update_not_configured'
  | 'batch_create_event'
  | 'batch_attribute_update'
  | 'in_app'
  | 'in_app_broadcast'
  | 'llm_action_prompt'
  | 'llm_action_result_schema'
  | 'line'
  | 'inbox'
  | 'whatsapp'
  // Newsletters are created with channel "unset" while the user picks a channel in a later setup
  // step. The backend indexes the underlying template with template_type "unset" before that happens.
  // https://github.com/customerio/ui/blob/main/app/pods/broadcasts/new/controller.ts#L113
  // https://github.com/customerio/services/blob/main/ui_api/newsletters/create.go#L42
  | 'unset';

interface TemplateResult extends BaseSearchResult {
  kind: 'template';
  other: {
    template_type: TemplateType;
    subject?: string;
    language?: string;
    action_id?: string;
    newsletter_id?: string;
    transactional_message_id?: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
  };
}

interface NavigationResult extends BaseSearchResult {
  kind: 'navigation';
  title: string;
  url: string;
  description?: never; // Navigation items don't have descriptions
  other?: never; // Navigation items don't have other
  snippets?: never; // Navigation items don't have snippets
}

interface NewsletterResult extends BaseSearchResult {
  kind: 'newsletter';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    sent: string;
  };
}

interface GoalResult extends BaseSearchResult {
  kind: 'goal';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    goal_event?: string;
  };
}

interface TransactionalMessageResult extends BaseSearchResult {
  kind: 'transactional_message';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    // Mirrors services/domains/transactional/message.go TransactionalMessage.TypeToString.
    // `twilio` is the legacy on-the-wire value for SMS.
    message_type?: 'email' | 'push' | 'in_app' | 'twilio' | 'inbox' | 'unknown';
    state?: 'draft' | 'active';
  };
}

// Mirrors services/domains/in_app_broadcasts/in_app_broadcasts.go State.String(). `deleting`
// is omitted because that state is tombstoned during indexing and never reaches search.
export type AnonymousInAppState = 'drafted' | 'scheduled' | 'starting' | 'running' | 'stopping' | 'expired' | 'stopped';

// Anonymous In-App Messages are backed by the in_app_broadcasts table. The search document
// shape comes from services/universal_search/anonymous_in_app.go (anonymousInAppRecord.Fields).
interface AnonymousInAppResult extends BaseSearchResult {
  kind: 'anonymous_in_app';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    state?: AnonymousInAppState;
  };
}

// Snippets are reusable Liquid fragments backed by the global_variables table. The search
// document shape comes from services/universal_search/snippets.go: title, url, and updated_at
// are indexed as fields, and the Liquid value is indexed as searchable content (surfaced as
// matched-text excerpts in the base `snippets` field).
interface SnippetResult extends BaseSearchResult {
  kind: 'snippet';
  other: {
    updated_at: string;
    env_id: string;
    id: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union.
    // Snippets only index title, url, and updated_at — there is no created_at.
    created_at?: undefined;
  };
}

// Email Layouts are the reusable HTML wrappers (header/footer around a {{content}} placeholder)
// backed by the email_layouts table. The search document shape comes from
// services/universal_search/email_layouts.go (emailLayoutRecord.Fields) — services serialises the
// layout's archived flag as the string "true"/"false".
interface EmailLayoutResult extends BaseSearchResult {
  kind: 'email_layout';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    archived?: 'true' | 'false';
  };
  // Email layouts are content-bearing — the backend indexes the HTML body, so search
  // returns highlighted body snippets via the inherited BaseSearchResult.snippets.
}

// Collections are reusable datasets (the collections table) imported from CSV / Google Sheets /
// URL / Amazon S3 and referenced by Liquid in messages. The search document shape comes from
// services/universal_search/collections.go (collectionRecord.Fields). The url is the Fly app link
// to the collection itself (not the import source url), so a result navigates into the workspace.
interface CollectionResult extends BaseSearchResult {
  kind: 'collection';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    // Import source kind: 's3' | 'sheet' | 'url'; empty for a manually-built collection.
    import_type?: string;
    // services serialises the bool via convertToSearchResult, so it arrives as "true"/"false".
    importing?: 'true' | 'false';
    // Row count as a numeric string (e.g. "1234"): convertToSearchResult only surfaces string/bool
    // struct fields, so services indexes it as a string to keep it retrievable in search results.
    rows_number?: string;
  };
  // Collections index only their metadata (name + description), not the row data, so search never
  // returns body snippets via the inherited BaseSearchResult.snippets.
}

export interface CustomerResult extends BaseSearchResult {
  kind: 'customer';
  other: {
    id?: string;
    email?: string;
    unsubscribed?: boolean;
    created_at?: string;
  };
}

export interface DocsResult extends BaseSearchResult {
  kind: 'docs';
  other: {
    description: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union
    created_at?: undefined;
  };
}

type IntegrationResultOther = {
  type: string;
  enabled: boolean;
  status: string;
  created_at?: number;
  last_activity?: number;
};

export interface SourceResult extends BaseSearchResult {
  kind: 'source';
  other: IntegrationResultOther;
}

export interface DestinationResult extends BaseSearchResult {
  kind: 'destination';
  other: IntegrationResultOther;
}

export interface CustomFormResult extends BaseSearchResult {
  kind: 'custom_form';
  other: {
    env_id: number;
    form_type: string;
    form_id: string;
    status: string;
    created_at?: number;
    last_activity_at?: number;
    html_form_url?: string;
  };
}

export interface ReportingWebhookResult extends BaseSearchResult {
  kind: 'reporting_webhook';
  other: {
    enabled: boolean;
    created_at?: number;
  };
}

export interface CdpOutResult extends BaseSearchResult {
  kind: 'cdp_out';
  other: {
    enabled: boolean;
    created_at?: number;
  };
}

export type DataOutExporterType = 'mixpanel' | 'amplitude' | 'segment_out' | 'mparticle';

export interface DataOutResult extends BaseSearchResult {
  kind: 'data_out';
  other: {
    exporter_type: DataOutExporterType;
    enabled: boolean;
    created_at?: number;
  };
}

export interface ObjectTypeResult extends BaseSearchResult {
  kind: 'object_type';
  other: {
    id: number;
    env_id: number;
    slug: string;
    singular_name: string;
    object_count: number;
    icon?: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union
    created_at?: undefined;
  };
}

export type SearchResult =
  | CampaignResult
  | ActionResult
  | SegmentResult
  | TemplateResult
  | NavigationResult
  | NewsletterResult
  | GoalResult
  | TransactionalMessageResult
  | AnonymousInAppResult
  | SnippetResult
  | EmailLayoutResult
  | CollectionResult
  | CustomerResult
  | DocsResult
  | SourceResult
  | DestinationResult
  | AdAudienceResult
  | CustomFormResult
  | ReportingWebhookResult
  | CdpOutResult
  | DataOutResult
  | ObjectTypeResult;

export type AdAudienceCredentialType = 'facebook' | 'adwords';

export interface AdAudienceResult extends BaseSearchResult {
  kind: 'ad_audience';
  other: {
    credential_type: AdAudienceCredentialType;
    expired: boolean;
    created_at?: number;
  };
}
